// Superadmin AI-quota editors, used inside AdminPortal:
//   • TenantQuotaPanel    — per-tenant overrides (in the tenant drawer)
//   • QuotaDefaultsPanel  — global per-plan defaults (its own tab)
// Both talk to the /admin/flags/.../quotas and /admin/quota-defaults endpoints.
import { useEffect, useState } from 'react'
import { api as axiosApi } from '../../lib/api'

type Toast = (msg: string, type?: 'success' | 'error') => void

interface QuotaInfo {
  flag: string; label: string; plan: string
  daily_default: number; monthly_default: number
  daily_override: number | null; monthly_override: number | null
  daily_limit: number; monthly_limit: number
  daily_used: number; monthly_used: number
  daily_reset_at: string; monthly_reset_at: string
}
interface QuotaDefault { flag: string; label: string; plan: string; daily_limit: number; monthly_limit: number }
interface AIFeature { key: string; label: string; description: string; live: boolean }

const PLANS = ['seedling', 'grower', 'estate'] as const
const limitText = (n: number) => (n === 0 ? '∞' : n.toLocaleString())
// '' → null (inherit); numeric (incl 0) → that value
const parseLimit = (s: string): number | null => {
  const t = s.trim()
  if (t === '') return null
  const n = Math.floor(Number(t))
  return Number.isFinite(n) && n >= 0 ? n : null
}

const card: React.CSSProperties = {
  background: 'var(--color-background-secondary)',
  border: '0.5px solid var(--color-border-tertiary)',
  borderRadius: 10, padding: '10px 12px',
}
const inp: React.CSSProperties = {
  width: 76, boxSizing: 'border-box', background: 'var(--color-background-primary)',
  border: '0.5px solid var(--color-border-tertiary)', borderRadius: 6,
  padding: '4px 7px', color: 'var(--color-text-primary)', fontSize: 12, outline: 'none',
}
const btn = (bg: string): React.CSSProperties => ({
  padding: '4px 10px', borderRadius: 6, border: 'none', background: bg,
  color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
})
const sectionLabel: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10,
}

// ── Per-tenant overrides ──────────────────────────────────────────────────────
export function TenantQuotaPanel({ tenantId, showToast }: { tenantId: string; showToast: Toast }) {
  const [rows, setRows] = useState<QuotaInfo[] | null>(null)
  // local edits keyed by flag: {daily, monthly} as raw input strings
  const [edit, setEdit] = useState<Record<string, { daily: string; monthly: string }>>({})
  const [busy, setBusy] = useState<string | null>(null)

  function load() {
    axiosApi.get(`/admin/flags/${tenantId}/quotas`).then(r => {
      const data: QuotaInfo[] = r.data || []
      setRows(data)
      const e: Record<string, { daily: string; monthly: string }> = {}
      data.forEach(q => { e[q.flag] = {
        daily: q.daily_override == null ? '' : String(q.daily_override),
        monthly: q.monthly_override == null ? '' : String(q.monthly_override),
      } })
      setEdit(e)
    }).catch(() => setRows([]))
  }
  useEffect(load, [tenantId])

  async function save(flag: string) {
    setBusy(flag)
    try {
      await axiosApi.put(`/admin/flags/${tenantId}/quotas/${flag}`, {
        daily_limit: parseLimit(edit[flag]?.daily ?? ''),
        monthly_limit: parseLimit(edit[flag]?.monthly ?? ''),
        reason: 'superadmin console',
      })
      showToast('Quota override saved'); load()
    } catch { showToast('Failed to save override', 'error') } finally { setBusy(null) }
  }
  async function clearOverride(flag: string) {
    setBusy(flag)
    try { await axiosApi.delete(`/admin/flags/${tenantId}/quotas/${flag}`); showToast('Reverted to plan default'); load() }
    catch { showToast('Failed to clear override', 'error') } finally { setBusy(null) }
  }
  async function resetUsage(flag: string) {
    setBusy(flag)
    try { await axiosApi.post(`/admin/flags/${tenantId}/quotas/${flag}/reset`); showToast('Usage reset'); load() }
    catch { showToast('Failed to reset usage', 'error') } finally { setBusy(null) }
  }

  if (!rows) return <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Loading quotas…</div>

  return (
    <div>
      <div style={sectionLabel}>AI quotas — daily / monthly (0 = unlimited; blank = inherit plan default)</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map(q => {
          const overridden = q.daily_override != null || q.monthly_override != null
          const bar = (used: number, limit: number) => {
            const pct = limit === 0 ? Math.min((used / Math.max(used, 1)) * 0, 0) : Math.min((used / limit) * 100, 100)
            const col = pct >= 95 ? '#F87171' : pct >= 80 ? '#F5A623' : '#2D6A2F'
            return (
              <div style={{ height: 4, borderRadius: 2, background: 'var(--color-background-primary)', overflow: 'hidden', marginTop: 3 }}>
                <div style={{ height: '100%', width: `${pct}%`, background: col, borderRadius: 2 }} />
              </div>
            )
          }
          return (
            <div key={q.flag} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {q.label}
                  {overridden && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: '#F5A623', border: '0.5px solid #F5A62366', borderRadius: 4, padding: '1px 4px' }}>OVERRIDE</span>}
                </span>
                <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>
                  plan default {limitText(q.daily_default)}/day · {limitText(q.monthly_default)}/mo
                </span>
              </div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <label style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>Daily<br />
                  <input style={inp} placeholder={`${q.daily_default}`} value={edit[q.flag]?.daily ?? ''}
                    onChange={e => setEdit(s => ({ ...s, [q.flag]: { ...s[q.flag], daily: e.target.value } }))} />
                </label>
                <label style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>Monthly<br />
                  <input style={inp} placeholder={`${q.monthly_default}`} value={edit[q.flag]?.monthly ?? ''}
                    onChange={e => setEdit(s => ({ ...s, [q.flag]: { ...s[q.flag], monthly: e.target.value } }))} />
                </label>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>
                    used: {q.daily_used}/{limitText(q.daily_limit)} day · {q.monthly_used}/{limitText(q.monthly_limit)} mo
                  </div>
                  {bar(q.daily_used, q.daily_limit)}
                  {bar(q.monthly_used, q.monthly_limit)}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button disabled={busy === q.flag} style={btn('#2D6A2F')} onClick={() => save(q.flag)}>Save</button>
                  <button disabled={busy === q.flag} style={btn('#555')} onClick={() => resetUsage(q.flag)}>Reset usage</button>
                  {overridden && <button disabled={busy === q.flag} style={{ ...btn('transparent'), color: '#F87171', border: '0.5px solid #F8717155' }} onClick={() => clearOverride(q.flag)}>Clear</button>}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Global per-plan defaults ──────────────────────────────────────────────────
export function QuotaDefaultsPanel({ showToast }: { showToast: Toast }) {
  const [features, setFeatures] = useState<AIFeature[]>([])
  const [defs, setDefs] = useState<Record<string, { daily: string; monthly: string }>>({}) // key `${flag}|${plan}`
  const [busy, setBusy] = useState<string | null>(null)

  function load() {
    axiosApi.get('/admin/quota-defaults').then(r => {
      setFeatures(r.data.features || [])
      const d: Record<string, { daily: string; monthly: string }> = {}
      ;(r.data.defaults as QuotaDefault[] || []).forEach(q => { d[`${q.flag}|${q.plan}`] = { daily: String(q.daily_limit), monthly: String(q.monthly_limit) } })
      setDefs(d)
    }).catch(() => setFeatures([]))
  }
  useEffect(load, [])

  async function save(flag: string, plan: string) {
    const k = `${flag}|${plan}`
    setBusy(k)
    try {
      await axiosApi.put(`/admin/quota-defaults/${flag}/${plan}`, {
        daily_limit: Math.max(0, Math.floor(Number(defs[k]?.daily) || 0)),
        monthly_limit: Math.max(0, Math.floor(Number(defs[k]?.monthly) || 0)),
      })
      showToast(`Default saved (${plan})`)
    } catch { showToast('Failed to save default', 'error') } finally { setBusy(null) }
  }

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>AI quota defaults</h2>
      <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 18, maxWidth: 640 }}>
        Per-plan ceilings every tenant inherits unless a per-tenant override is set (edit those from a tenant's drawer).
        Two periods per feature: <strong>daily</strong> (anti-abuse cap) and <strong>monthly</strong>. 0 = unlimited.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {features.map(f => (
          <div key={f.key} style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{f.label}</span>
              {!f.live && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--color-text-tertiary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: 4, padding: '1px 4px' }}>NOT YET ACTIVE</span>}
              <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{f.description}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {PLANS.map(plan => {
                const k = `${f.key}|${plan}`
                return (
                  <div key={plan} style={{ background: 'var(--color-background-primary)', borderRadius: 8, padding: '8px 10px', border: '0.5px solid var(--color-border-tertiary)' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#F5A623', textTransform: 'capitalize', marginBottom: 6 }}>{plan}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                      <label style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>Daily<br />
                        <input style={inp} value={defs[k]?.daily ?? ''} onChange={e => setDefs(s => ({ ...s, [k]: { ...s[k], daily: e.target.value } }))} />
                      </label>
                      <label style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>Monthly<br />
                        <input style={inp} value={defs[k]?.monthly ?? ''} onChange={e => setDefs(s => ({ ...s, [k]: { ...s[k], monthly: e.target.value } }))} />
                      </label>
                      <button disabled={busy === k} style={btn('#2D6A2F')} onClick={() => save(f.key, plan)}>Save</button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
